/**
 * Helpdesk chat widget loader.
 *
 * Paste this into a host page:
 *   <script async src="https://chat.airclou.com/loader.js"
 *           data-tenant-id="ten_..." data-workspace-id="wks_..."></script>
 *
 * Architecture (LIVE_CHAT_SPEC.md §5.1, Intercom pattern):
 *   The widget UI is rendered into a Shadow DOM root attached to the
 *   host page — no iframe wrapper. Why:
 *     - Drop shadow on the chat panel renders end-to-end (no iframe edge
 *       to clip).
 *     - Pointer events flow through everywhere the panel/launcher aren't
 *       drawn (no iframe rectangle absorbing clicks).
 *     - Host-page-native focus order, viewport units, drag-and-drop.
 *   Style isolation: Shadow DOM. CSS inheritance is scrubbed by a
 *   `:host { all: initial }` reset in widget.css.
 *
 *   Token mint stays here (host page) so the backend's origin check keys
 *   on the host's domain. Otherwise every tenant would just register
 *   chat.airclou.com. The loader hands the minted token into mount() and
 *   exposes a `requestTokenRefresh` callback the widget invokes when its
 *   §5.1 rotation path needs a new token.
 *
 * Output: built as an IIFE at dist/loader.js via vite.loader.config.ts.
 */

interface SessionTokenResponse {
  token: string;
  expiresAt: string;
  visitorId: string;
  jti: string;
  /** Present when the visitor previously identified. Carried through to the
   *  widget so it doesn't re-prompt for email on a silent token rotation. */
  contactId?: string | null;
  /** Same value as the `chat_widget_bind` cookie the mint sets. Passed
   *  straight through to the widget, which falls back to sending it as the
   *  `X-Widget-Bind` header in browsers that refuse to store the cookie
   *  (Safari). Declared here only so the pass-through is visible. */
  bindValue?: string;
  /** Signed visitor-identity attestation — the value of the
   *  `chat_widget_vid` cookie. Present when the cookie carrier did NOT prove
   *  this mint's identity, meaning either a first-ever load or a browser that
   *  blocks the cookie; the loader stores it and presents it as
   *  `X-Widget-Visitor` on later mints. Explicitly `null` once the cookie is
   *  proven to work, which is the signal to drop the stored copy. */
  visitorAuth?: string | null;
}

interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: { code?: string; message?: string };
}

/** Public surface of the widget bundle (matches main.tsx). Kept inline so
 *  the loader has no source-level dependency on the widget package. */
interface WidgetMountOptions {
  tenantId: string;
  workspaceId: string;
  backendUrl: string;
  realtimeUrl: string;
  token: SessionTokenResponse;
  onRequestTokenRefresh: () => Promise<SessionTokenResponse | null>;
  initialParentUrl: string;
}
interface WidgetHandle {
  notifyParentUrl(url: string): void;
  unmount(): void;
}
interface WidgetModule {
  mount(root: ShadowRoot | Element, opts: WidgetMountOptions): WidgetHandle;
}

function log(level: 'warn' | 'error', msg: string, meta?: unknown): void {
  const prefix = '[helpdesk-widget]';
  if (level === 'warn') console.warn(prefix, msg, meta);
  else console.error(prefix, msg, meta);
}

function resolveScriptTag(): HTMLScriptElement | null {
  if (document.currentScript && document.currentScript instanceof HTMLScriptElement) {
    return document.currentScript;
  }
  // Fallback: pick the last <script> with our loader URL.
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[src*="loader.js"]');
  return scripts[scripts.length - 1] ?? null;
}

import { scoped } from './storageKeys';

function visitorStorageKey(tenantId: string, workspaceId: string): string {
  return scoped(tenantId, workspaceId, 'visitorId');
}

function readVisitorId(tenantId: string, workspaceId: string): string | null {
  try {
    return localStorage.getItem(visitorStorageKey(tenantId, workspaceId));
  } catch {
    return null;
  }
}

function writeVisitorId(tenantId: string, workspaceId: string, visitorId: string): void {
  try {
    localStorage.setItem(visitorStorageKey(tenantId, workspaceId), visitorId);
  } catch {
    // Quota exceeded or storage disabled; losing visitor persistence is
    // acceptable — the widget will mint a fresh one next time.
  }
}

// ─── visitor-identity attestation (cookie-less browsers) ──────────────────
//
// The visitorId in localStorage above is a hint the server ignores — it is
// not secret, so the mint refuses to take a visitor's word for who they are
// (M5). What the mint *does* accept is the server-signed attestation it set
// as the `chat_widget_vid` cookie. On Safari that cookie is third-party and
// gets dropped, so every mint saw a brand-new browser and handed back a new
// visitorId — and since conversation history is scoped to visitorId, the
// visitor's thread vanished on every refresh.
//
// So the mint also returns the attestation in its body when the cookie didn't
// carry the identity. We keep it here and present it as `X-Widget-Visitor` on
// subsequent mints. It is a real credential, so we hold it only for as long as
// the server says we need to: a mint that answers `visitorAuth: null` has
// proven the cookie works, and we drop our copy on the spot.

function visitorAuthStorageKey(tenantId: string, workspaceId: string): string {
  return scoped(tenantId, workspaceId, 'visitorAuth');
}

function readVisitorAuth(tenantId: string, workspaceId: string): string | null {
  try {
    return localStorage.getItem(visitorAuthStorageKey(tenantId, workspaceId));
  } catch {
    return null;
  }
}

/** Persist the attestation, or clear it when the server reports the cookie is
 *  doing its job (`visitorAuth: null`). A response that omits the field
 *  entirely is an older backend — leave whatever we have alone. */
function syncVisitorAuth(
  tenantId: string,
  workspaceId: string,
  visitorAuth: string | null | undefined,
): void {
  if (visitorAuth === undefined) return;
  try {
    const key = visitorAuthStorageKey(tenantId, workspaceId);
    if (visitorAuth) localStorage.setItem(key, visitorAuth);
    else localStorage.removeItem(key);
  } catch {
    // Storage disabled or full. The visitor falls back to the pre-fix
    // behaviour — a fresh identity per load — rather than breaking.
  }
}

// Codes that mean "visitor really is blocked" — retrying won't help, and
// would be noise. Anything else (5xx, timeout, transient fetch failure)
// gets retried so a brief blip doesn't leave the visitor with no widget.
const HARD_MINT_CODES = new Set<string>([
  'origin_not_registered',
  'origin_required',
  'widget_ip_banned',
  // widget-help-tab.md §5.1 — a tampered or disabled data-brand-id is
  // a deliberate misconfiguration; retrying won't fix it.
  'invalid_brand',
  // Phase 2 strict triple — snippet didn't supply data-brand-id.
  // Deterministic; retrying won't materialise the missing attribute.
  'brand_id_required',
  // Brand exists but is paused (is_active = FALSE). Visitor's chat
  // is blocked by operator action; retry would just be noise.
  'brand_paused',
]);

async function mint(
  backendUrl: string,
  tenantId: string,
  workspaceId: string,
  visitorId: string | null,
  oldToken: string | null = null,
  brandId: string | null = null,
): Promise<SessionTokenResponse> {
  // Sent whenever we hold one. The server prefers its own cookie and only
  // falls back to this, so a browser with a working cookie is unaffected —
  // and it answers `visitorAuth: null`, after which we stop sending it.
  const visitorAuth = readVisitorAuth(tenantId, workspaceId);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15_000);
  let response: Response;
  try {
    response = await fetch(`${backendUrl}/api/chat/widget/session-token`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        // §5.1: when rotating, include the old token so the server can revoke
        // its jti. Omitted on first-time mint.
        ...(oldToken ? { Authorization: `Bearer ${oldToken}` } : {}),
        ...(visitorAuth ? { 'X-Widget-Visitor': visitorAuth } : {}),
      },
      body: JSON.stringify({
        tenantId,
        workspaceId,
        ...(visitorId ? { visitorId } : {}),
        // widget-help-tab.md §5.1 — Help-tab brand selection. Server
        // validates against helpdesk_brands for this workspace AND
        // help_center_enabled. Tampered → 400 invalid_brand (which is
        // a HARD_MINT_CODE so we don't retry indefinitely).
        ...(brandId ? { brandId } : {}),
      }),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }

  let parsed: Envelope<SessionTokenResponse> | null = null;
  try {
    parsed = await response.json();
  } catch {
    // Non-JSON response — will fall through to the error path.
  }

  if (!response.ok || !parsed || !parsed.success || !parsed.data) {
    const code = parsed?.error?.code ?? `http_${response.status}`;
    throw new Error(code);
  }
  return parsed.data;
}

/**
 * Mint with bounded retry + jittered exponential backoff. Critical-path
 * invariant: the launcher must appear for any visitor on a good network.
 * A single transient 5xx / fetch abort must not permanently hide the widget.
 */
async function mintWithRetry(
  backendUrl: string,
  tenantId: string,
  workspaceId: string,
  visitorId: string | null,
  oldToken: string | null = null,
  brandId: string | null = null,
): Promise<SessionTokenResponse> {
  const backoffs = [0, 1_000, 3_000, 7_000, 15_000];
  let lastErr: unknown = null;
  for (let i = 0; i < backoffs.length; i++) {
    if (backoffs[i] > 0) {
      const jitter = backoffs[i] * (0.8 + Math.random() * 0.4);
      await new Promise((r) => setTimeout(r, Math.round(jitter)));
    }
    try {
      return await mint(backendUrl, tenantId, workspaceId, visitorId, oldToken, brandId);
    } catch (err) {
      lastErr = err;
      const code = err instanceof Error ? err.message : '';
      if (HARD_MINT_CODES.has(code)) throw err;
      log('warn', `mint attempt ${i + 1} failed; retrying`, err);
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('mint_failed');
}

interface InjectOptions {
  widgetOrigin: string;
  tenantId: string;
  workspaceId: string;
  backendUrl: string;
  realtimeUrl: string;
  token: SessionTokenResponse;
  /** Loader-supplied data-brand-id, threaded into rotation calls so the
   *  resolved brand stays stable across silent token refreshes
   *  (otherwise multi-brand workspaces would lose Help on rotation when
   *  auto-resolve sees ambiguity). null when no explicit brand was set. */
  brandId: string | null;
}

const HOST_ELEMENT_ID = 'airclou-helpdesk-widget-host';

/**
 * Drop the host element + shadow root + history hooks. Used by the boot
 * watchdog when re-trying a hung mount, and exposed via window for
 * deliberate teardown by host pages that hot-swap widget configs.
 */
function teardownExisting(): void {
  const existing = document.getElementById(HOST_ELEMENT_ID);
  if (!existing) return;
  try { existing.remove(); } catch { /* ignore */ }
}

/**
 * Install host-page history hooks so SPA navigations (pushState /
 * replaceState / popstate / hashchange) reach the widget's proactive
 * runtime as URL changes. Returns a teardown that restores the patched
 * methods and removes the listeners.
 */
function installHistoryHooks(onChange: (url: string) => void): () => void {
  const fire = () => {
    try { onChange(window.location.href); }
    catch (err) { log('warn', 'history listener threw', err); }
  };
  let origPush: typeof history.pushState | null = null;
  let origReplace: typeof history.replaceState | null = null;
  try {
    origPush = history.pushState;
    origReplace = history.replaceState;
    history.pushState = function (...args: Parameters<typeof history.pushState>): void {
      origPush!.apply(history, args);
      fire();
    };
    history.replaceState = function (...args: Parameters<typeof history.replaceState>): void {
      origReplace!.apply(history, args);
      fire();
    };
  } catch (err) {
    // Some sandboxed environments throw on history mutation; degrade to
    // popstate-only.
    log('warn', 'failed to hook history.pushState/replaceState', err);
  }
  window.addEventListener('popstate', fire);
  window.addEventListener('hashchange', fire);
  return () => {
    try {
      if (origPush) history.pushState = origPush;
      if (origReplace) history.replaceState = origReplace;
    } catch { /* ignore */ }
    window.removeEventListener('popstate', fire);
    window.removeEventListener('hashchange', fire);
  };
}

const WIDGET_BUNDLE_PATH = '/widget.js';
// Stamped at build time by vite.loader.config.ts. Appended to widget.js
// as `?v=…` so each deploy busts host browsers' cached widget.js even
// though the filename itself is fixed (Vite lib mode doesn't hash it).
declare const __WIDGET_BUILD_ID__: string;
const WIDGET_BUILD_ID: string =
  typeof __WIDGET_BUILD_ID__ !== 'undefined' ? __WIDGET_BUILD_ID__ : 'dev';
// Boot watchdog: if the widget bundle fails to load/execute, retry once
// with a cache-bust before giving up. Same critical-path invariant as the
// mint retry — a single transient 5xx / parse error / CSP blip must not
// permanently hide the launcher.
const BOOT_WATCHDOG_MS = 30_000;

async function importWidgetBundle(widgetOrigin: string, cacheBust: boolean): Promise<WidgetModule> {
  const url = new URL(WIDGET_BUNDLE_PATH, widgetOrigin);
  url.searchParams.set('v', WIDGET_BUILD_ID);
  if (cacheBust) url.searchParams.set('_retry', String(Date.now()));
  // Race the import against a watchdog. A `fetch` for a hung asset can sit
  // open for the host's network timeout, leaving the launcher invisible.
  let timer: ReturnType<typeof setTimeout> | null = null;
  const watchdog = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('widget_import_timeout')), BOOT_WATCHDOG_MS);
  });
  try {
    const mod = await Promise.race([
      import(/* @vite-ignore */ url.href),
      watchdog,
    ]);
    if (!mod || typeof (mod as WidgetModule).mount !== 'function') {
      throw new Error('widget_module_invalid');
    }
    return mod as WidgetModule;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function bootWidget(opts: InjectOptions): Promise<void> {
  // Defensive: if a previous loader run left a host element behind (HMR,
  // double-injection in dev), drop it before mounting the new one. Two
  // floating widgets on the same page is worse than a brief flash.
  teardownExisting();

  const host = document.createElement('div');
  host.id = HOST_ELEMENT_ID;
  // The host element itself participates in host-page layout — make sure
  // it can't accidentally take up space or shift content. Position-fixed
  // children inside the shadow root are positioned against the viewport,
  // so the host can stay 0×0.
  host.style.cssText = [
    'all: initial',
    'position: fixed',
    'top: 0',
    'left: 0',
    'width: 0',
    'height: 0',
    'z-index: 2147483647',
    // Pointer events are gated per-node inside the shadow root via the
    // `:host` reset and the launcher/panel rules. The host itself takes
    // no events because it has no painted area.
  ].join('; ');
  document.body.appendChild(host);

  // closed mode keeps host-page JS from mucking with the widget's DOM —
  // matches Intercom's posture. The widget's React root inside has no
  // need to access the shadow root from outside.
  const shadow = host.attachShadow({ mode: 'closed' });

  let bundle: WidgetModule;
  try {
    bundle = await importWidgetBundle(opts.widgetOrigin, false);
  } catch (err) {
    log('warn', 'widget bundle import failed; retrying once', err);
    try {
      bundle = await importWidgetBundle(opts.widgetOrigin, true);
    } catch (retryErr) {
      log(
        'error',
        'widget bundle failed to load after retry; giving up. ' +
          'Check that the asset is reachable and not blocked by CSP.',
        retryErr,
      );
      teardownExisting();
      return;
    }
  }

  // Track the latest token so subsequent rotations can pass it as
  // `oldToken` for jti revocation (§5.1).
  let currentToken: SessionTokenResponse = opts.token;

  const handle = bundle.mount(shadow, {
    tenantId: opts.tenantId,
    workspaceId: opts.workspaceId,
    backendUrl: opts.backendUrl,
    realtimeUrl: opts.realtimeUrl,
    token: currentToken,
    onRequestTokenRefresh: async () => {
      try {
        const existingVisitor = readVisitorId(opts.tenantId, opts.workspaceId);
        const newToken = await mintWithRetry(
          opts.backendUrl,
          opts.tenantId,
          opts.workspaceId,
          existingVisitor,
          currentToken.token,
          opts.brandId,
        );
        currentToken = newToken;
        writeVisitorId(opts.tenantId, opts.workspaceId, newToken.visitorId);
        syncVisitorAuth(opts.tenantId, opts.workspaceId, newToken.visitorAuth);
        return newToken;
      } catch (err) {
        log('warn', 'token refresh failed', err);
        return null;
      }
    },
    initialParentUrl: window.location.href,
  });

  // Re-post the parent URL on every SPA navigation so client-side
  // `url_match` rules see the current page, not just the page the visitor
  // landed on.
  installHistoryHooks((url) => handle.notifyParentUrl(url));
}

function boot(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const script = resolveScriptTag();
  if (!script) {
    log('error', 'could not locate loader <script> tag');
    return;
  }

  const tenantId = script.getAttribute('data-tenant-id') ?? '';
  const workspaceId = script.getAttribute('data-workspace-id') ?? '';
  if (!tenantId || !workspaceId) {
    log('error', 'missing data-tenant-id or data-workspace-id on loader script');
    return;
  }
  // widget-help-tab.md §5.1 — optional explicit brand id for multi-brand
  // workspaces. When omitted, server auto-resolves a single eligible
  // brand or leaves it null (Help tab hidden).
  const brandId = script.getAttribute('data-brand-id') || null;

  const widgetOrigin = (() => {
    try {
      return new URL(script.src).origin;
    } catch {
      return '';
    }
  })();
  if (!widgetOrigin) {
    log('error', 'could not resolve widget origin from script src');
    return;
  }

  const backendUrl = script.getAttribute('data-backend-url') ?? widgetOrigin.replace(
    /^https:\/\/chat\./,
    'https://app.',
  );
  const realtimeUrl = script.getAttribute('data-realtime-url') ?? backendUrl.replace(
    /^https:/,
    'wss:',
  ) + '/helpdesk/ws';

  const existingVisitorId = readVisitorId(tenantId, workspaceId);

  mintWithRetry(backendUrl, tenantId, workspaceId, existingVisitorId, null, brandId).then(
    (token) => {
      writeVisitorId(tenantId, workspaceId, token.visitorId);
      syncVisitorAuth(tenantId, workspaceId, token.visitorAuth);
      void bootWidget({
        widgetOrigin,
        tenantId,
        workspaceId,
        backendUrl,
        realtimeUrl,
        token,
        brandId,
      });
    },
    (err) => {
      log('warn', 'failed to mint session token; widget not loaded', err);
      // mintWithRetry already exhausted its ~26s backoff window, so the
      // visitor's network was down longer than that. Arm recovery listeners
      // so boot() runs again the moment the browser reports connectivity or
      // the tab is refocused. Without this, a transient outage longer than
      // the retry window permanently hides the widget until a full page
      // reload — a critical-path violation.
      const code = err instanceof Error ? err.message : '';
      if (HARD_MINT_CODES.has(code)) return; // deterministic — no point retrying
      let recovering = false;
      const retry = () => {
        if (recovering) return;
        recovering = true;
        window.removeEventListener('online', retry);
        document.removeEventListener('visibilitychange', onVisibility);
        boot();
      };
      const onVisibility = () => {
        if (document.visibilityState === 'visible') retry();
      };
      window.addEventListener('online', retry);
      document.addEventListener('visibilitychange', onVisibility);
    },
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
